import * as React from "react";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

import { getFirstFqlTicker } from "../../ai/tableHelpers";
import type { DocumentSourceItem, SourceItem, TableSourceItem } from "../../ai/sourceItems";
import { getCitationColor } from "../colors";
import {
  formatSourceCardDate,
  formatSourceLabel,
  getSourceCardTableTitle,
  getTableEntityDisplay,
} from "../content/sourceDisplay";

interface SourceCardProps {
  item: SourceItem;
  /** Per-message map of inline citation UUID → displayed cited value. */
  citedValuesByUuid?: Record<string, string>;
  onView: (item: SourceItem) => void;
  onCitationView?: (id: string) => void;
  compact?: boolean;
  openOnCardClick?: boolean;
}

/**
 * Card surface for a single source item — either a Dictionary document
 * (SEC filing, web page, IR doc) or a Markdown citation table
 * (FactSet Fundamentals / Estimates).
 *
 * Replaces the per-uuid `CitationCard` that used to render raw
 * `<iframe>` HTML and ASCII markdown-table fragments. Matches the
 * card pattern from finster-chat's
 * `app/(main)/explorer/components/Session/Citation.tsx`.
 */
export const SourceCard: React.FC<SourceCardProps> = ({
  item,
  citedValuesByUuid,
  onView,
  onCitationView,
  compact = false,
  openOnCardClick = false,
}) => {
  if (item.kind === "document") {
    return (
      <DocumentCard
        item={item}
        citedValuesByUuid={citedValuesByUuid}
        onView={onView}
        onCitationView={onCitationView}
        compact={compact}
        openOnCardClick={openOnCardClick}
      />
    );
  }
  return (
    <TableCard
      item={item}
      citedValuesByUuid={citedValuesByUuid}
      onView={onView}
      onCitationView={onCitationView}
      compact={compact}
      openOnCardClick={openOnCardClick}
    />
  );
};

interface DocumentCardProps {
  item: DocumentSourceItem;
  citedValuesByUuid?: Record<string, string>;
  onView: (item: SourceItem) => void;
  onCitationView?: (id: string) => void;
  compact?: boolean;
  openOnCardClick?: boolean;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  item,
  citedValuesByUuid,
  onView,
  onCitationView,
  compact = false,
  openOnCardClick = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const metadata = item.link.metadata;
  const dataSource = metadata?.dataSource ?? item.envelope.metadata?.dataSource;
  const color = getCitationColor(dataSource ?? item.label ?? item.client ?? null);

  const title = metadata?.documentName?.trim() || item.link.link || "Untitled source";
  const company = metadata?.companyName;
  const date = formatSourceCardDate(metadata?.datePublished);

  const citedValues = pickCitedValues(item.citedUuids, citedValuesByUuid, item.citedUuids.length);

  return (
    <div
      role="group"
      className="group/source-card relative flex w-full min-w-0 flex-col overflow-hidden rounded-md border border-border/70 bg-background transition-colors duration-150 hover:border-border"
      style={{ borderLeftColor: color, borderLeftWidth: 2 }}
    >
      <button type="button" onClick={() => onView(item)} className="sr-only">
        View source
      </button>
      <button
        type="button"
        onClick={() =>
          openOnCardClick ? onView(item) : setOpen((next) => !next)
        }
        className={`flex w-full cursor-pointer flex-col border-0 bg-transparent text-left ${
          compact ? "gap-0.5 px-2 py-1.5" : "gap-1.5 p-2.5"
        }`}
        aria-expanded={openOnCardClick ? undefined : open}
      >
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <p
              className={`line-clamp-2 min-w-0 font-semibold leading-snug text-foreground ${
                compact ? "text-[11.5px]" : "text-[13px]"
              }`}
              title={title}
            >
              {title}
            </p>
            {company && (
              <p className={`truncate text-muted-foreground ${
                compact ? "text-[9.5px] leading-[12px]" : "text-[10.5px] leading-[14px]"
              }`}>
                {company}
              </p>
            )}
          </div>
          <span className={`mt-0.5 shrink-0 rounded-md text-muted-foreground transition-colors group-hover/source-card:bg-muted/40 group-hover/source-card:text-foreground ${
            compact ? "p-0.5" : "p-1"
          }`}>
            {open ? (
              <ChevronDown className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} transition-transform duration-200`} />
            ) : (
              <ChevronRight className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} transition-transform duration-200 group-hover/source-card:translate-x-0.5`} />
            )}
          </span>
        </div>

        <SourceSummary
          citedCount={citedValues.length}
          date={date}
          compact={compact}
        />
      </button>

      {open && (
        <ExpandedCitationList
          itemId={item.id}
          citedValues={citedValues}
          color={color}
          onCitationView={onCitationView}
          onView={() => onView(item)}
          compact={compact}
        />
      )}
    </div>
  );
};

interface ExpandedCitationListProps {
  itemId: string;
  citedValues: CitedValue[];
  color: string;
  onCitationView?: (id: string) => void;
  onView: () => void;
  compact?: boolean;
}

const ExpandedCitationList: React.FC<ExpandedCitationListProps> = ({
  itemId,
  citedValues,
  color,
  onCitationView,
  onView,
  compact = false,
}) => {
  return (
    <div className={`animate-in fade-in-0 slide-in-from-top-1 border-t border-border/40 bg-muted/[0.14] duration-150 ${
      compact ? "px-2 pb-2 pt-1.5" : "px-3 pb-3 pt-2"
    }`}>
      {citedValues.length > 0 ? (
        <ol className="flex flex-col gap-1.5">
          {citedValues.map((citation, index) => (
            <li key={`${itemId}-cited-expanded-${citation.id}`} className="flex gap-2.5">
              <span className={`${compact ? "h-4 w-4 text-[9px]" : "h-5 w-5 text-[10px]"} mt-[3px] flex shrink-0 items-center justify-center rounded-full bg-background font-medium text-muted-foreground ring-1 ring-border/70`}>
                {index + 1}
              </span>
              <button
                type="button"
                onClick={() => onCitationView?.(citation.id)}
                className={`min-w-0 flex-1 cursor-pointer rounded-md border-0 bg-transparent px-0 py-0.5 text-left italic leading-relaxed text-foreground/85 transition-colors hover:text-foreground ${
                  compact ? "text-[11px]" : "text-[12px]"
                }`}
                title={citation.value}
              >
                <CitedContextText citation={citation} color={color} />
              </button>
            </li>
          ))}
        </ol>
      ) : null}
      <div className={`${compact ? "mt-2" : "mt-2.5"} flex justify-end`}>
        <button
          type="button"
          onClick={onView}
          className={`inline-flex cursor-pointer items-center gap-1 rounded-md border-0 bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90 ${
            compact ? "px-2 py-1 text-[10px]" : "px-2.5 py-1.5 text-[11px]"
          }`}
        >
          View
          <ArrowRight className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
        </button>
      </div>
    </div>
  );
};

interface TableCardProps {
  item: TableSourceItem;
  citedValuesByUuid?: Record<string, string>;
  onView: (item: SourceItem) => void;
  onCitationView?: (id: string) => void;
  compact?: boolean;
  openOnCardClick?: boolean;
}

const TableCard: React.FC<TableCardProps> = ({
  item,
  citedValuesByUuid,
  onView,
  onCitationView,
  compact = false,
  openOnCardClick = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const dataSource = item.metadata?.dataSource;
  const color = getCitationColor(dataSource ?? item.label ?? item.client ?? null);
  const sourceLabel = formatSourceLabel(item.label || dataSource || item.client);
  const ticker = item.metadata?.ticker ?? getFirstFqlTicker(item);

  // Surface only values the model declared on `modifications.citations[]`.
  const citedValues = pickCitedValues(
    item.citedUuids,
    citedValuesByUuid,
    item.citedUuids.length
  );
  const entityDisplay = getTableEntityDisplay(item);
  const title = entityDisplay.summary ?? getSourceCardTableTitle(item, sourceLabel);
  const subtitle = entityDisplay.summary ? sourceLabel : ticker ?? "";

  return (
    <div
      role="group"
      className="group/source-card relative flex w-full min-w-0 flex-col overflow-hidden rounded-md border border-border/70 bg-background transition-colors duration-150 hover:border-border"
      style={{ borderLeftColor: color, borderLeftWidth: 2 }}
    >
      <button type="button" onClick={() => onView(item)} className="sr-only">
        View source
      </button>
      <button
        type="button"
        onClick={() =>
          openOnCardClick ? onView(item) : setOpen((next) => !next)
        }
        className={`flex w-full cursor-pointer flex-col border-0 bg-transparent text-left ${
          compact ? "gap-0.5 px-2 py-1.5" : "gap-1.5 p-2.5"
        }`}
        aria-expanded={openOnCardClick ? undefined : open}
      >
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className={`line-clamp-2 min-w-0 font-semibold leading-snug text-foreground ${
              compact ? "text-[11.5px]" : "text-[13px]"
            }`}>
              {title}
            </p>
            {subtitle && (
              <p className={`truncate text-muted-foreground ${
                compact ? "text-[9.5px] leading-[12px]" : "text-[10.5px] leading-[14px]"
              }`}>
                {subtitle}
              </p>
            )}
          </div>
          <span className={`mt-0.5 shrink-0 rounded-md text-muted-foreground transition-colors group-hover/source-card:bg-muted/40 group-hover/source-card:text-foreground ${
            compact ? "p-0.5" : "p-1"
          }`}>
            {open ? (
              <ChevronDown className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} transition-transform duration-200`} />
            ) : (
              <ChevronRight className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} transition-transform duration-200 group-hover/source-card:translate-x-0.5`} />
            )}
          </span>
        </div>
        <SourceSummary
          citedCount={citedValues.length}
          noun="value"
          compact={compact}
        />
      </button>

      {open && (
        <ExpandedCitationList
          itemId={item.id}
          citedValues={citedValues}
          color={color}
          onCitationView={onCitationView}
          onView={() => onView(item)}
          compact={compact}
        />
      )}
    </div>
  );
};

interface CitedValue {
  id: string;
  value: string;
}

function pickCitedValues(
  uuids: string[],
  map: Record<string, string> | undefined,
  limit = 3
): CitedValue[] {
  if (!map || uuids.length === 0) return [];
  const out: CitedValue[] = [];
  const seen = new Set<string>();
  for (const id of uuids) {
    if (out.length >= limit) break;
    const value = map[id];
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push({ id, value });
  }
  return out;
}

const SourceSummary: React.FC<{
  citedCount: number;
  date?: string;
  noun?: "passage" | "value";
  compact?: boolean;
}> = ({ citedCount, date, noun = "passage", compact = false }) => {
  return (
    <div className={`flex w-full items-center justify-between gap-2 pt-0.5 text-muted-foreground ${
      compact ? "text-[9.5px]" : "text-[11px]"
    }`}>
      <span>
        {citedCount} cited {noun}
        {citedCount === 1 ? "" : "s"}
      </span>
      {date && (
        <span className="inline-flex items-center gap-1">
          <Calendar className="h-3 w-3" aria-hidden />
          {date}
        </span>
      )}
    </div>
  );
};

const CitedContextText: React.FC<{ citation: CitedValue; color: string }> = ({
  citation,
  color,
}) => {
  return (
    <span
      className="font-medium italic underline decoration-[1.5px] underline-offset-[3px]"
      style={{ textDecorationColor: color }}
    >
      {citation.value}
    </span>
  );
};
